import React, { useState } from 'react';
import { Icon } from './Icon';
import type { HeaderProps } from '../types';
import { useAuth } from '../context/AuthContext';
import type { User } from '../types/auth';

export const Header: React.FC<HeaderProps> = ({
  darkMode,
  toggleDarkMode,
  toggleSidebar,
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const { logout } = useAuth(); // ✅ Obtener función logout
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const userData: User | null = (() => {
      try {
        const userStr = localStorage.getItem("user");
        return userStr ? JSON.parse(userStr) : null;
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
        return null;
      }
    })();

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (window.confirm('¿Estás seguro de que quieres cerrar sesión?')) {
      setIsLoggingOut(true);
      try {
        await logout();
        // La redirección se maneja automáticamente en el AuthContext
      } catch (error) {
        console.error('Error al cerrar sesión:', error);
        alert('Error al cerrar sesión. Inténtalo de nuevo.');
      } finally {
        setIsLoggingOut(false);
      }
    }
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-lg border-b border-gray-200 dark:border-gray-700 transition-colors duration-200 sticky top-0 z-40">
      <div className="flex items-center justify-between px-4 py-2">
        {/* Left section */}
        <div className="flex items-center space-x-4">
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            aria-label="Toggle sidebar"
          >
            <Icon name="menu" />
          </button>
        </div>

        {/* Right section */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Icon name="sun" /> : <Icon name="moon" />}
          </button>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {userData?.name?.[0] || 'U'}
              </div>
              <Icon name="chevron-down" className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </button>

            {/* User dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {userData?.name || 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {userData?.email || 'usuario@ejemplo.com'}
                  </p>
                </div>
                <hr className="my-2 border-gray-200 dark:border-gray-700" />

                {/* ✅ BOTÓN DE LOGOUT CON LÓGICA */}
                <button
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isLoggingOut ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-red-600 rounded-full border-t-transparent mr-2" />
                      Cerrando sesión...
                    </>
                  ) : (
                    'Cerrar Sesión'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside handlers */}
      {(showNotifications || showUserMenu) && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => {
            setShowNotifications(false);
            setShowUserMenu(false);
          }}
        />
      )}
    </header>
  );
};

export default Header;